// utils/displayFormatters.js
import { PhoneIcon, ClipboardDocumentListIcon, QuestionMarkCircleIcon, ChatBubbleOvalLeftIcon, HashtagIcon } from "@heroicons/react/24/outline";

export const formatLeadSource = (source) => {
  const sourceMapping = {
    google: "Google",
    facebook: "Facebook",
    instagram: "Instagram",
    youtube: "YouTube",
    other: "Other Social Media",
    preferral: "Patient Referral",
    ireferral: "Insurance Referral",
    sreferral: "Staff Referral",
    dreferral: "Doctor Referral",
    signage: "Signage/Walk by",
    flyer: "Flyer/Postcard",
    seminar: "Seminar",
    tradeshow: "Tradeshow/Community Event",
    na: "Not Applicable",
  };

  return sourceMapping[source] || source;
};

export const formatLeadTypeToIcon = (type) => {
  if (!type) return '';

  switch (type) {
    case 'phonecall':
      return (
        <span title="Phone" className="text-primary">
          <PhoneIcon className="h-5 w-5 inline" />
        </span>
      );
    case 'chatbot':
      return (
        <span title="Chatbot" className="text-primary">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5 inline">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.5V7" />
            <circle cx="12" cy="3" r="0.75" fill="currentColor" stroke="none" />
            <rect x="3" y="7" width="18" height="14" rx="2" />
            <circle cx="9" cy="12" r="1.75" fill="currentColor" stroke="none" />
            <circle cx="15" cy="12" r="1.75" fill="currentColor" stroke="none" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 16h7" />
          </svg>
        </span>
      );
    case 'contact_form':
      return (
        <span title="Webform" className="text-primary">
          <ClipboardDocumentListIcon className="h-5 w-5 inline" />
        </span>
      );
    case 'sms':
      return (
        <span title="SMS" className="text-primary">
          <ChatBubbleOvalLeftIcon className="h-5 w-5 inline" />
        </span>
      );
    case 'social':
      return (
        <span title="Social Media" className="text-primary">
          <HashtagIcon className="h-5 w-5 inline" />
        </span>
      );
    default:
      return (
        <span title="Unknown" className="text-gray-500">
          <QuestionMarkCircleIcon className="h-5 w-5 inline" />
        </span>
      );
  }
};

export const formatLeadType = (type) => {
  if (!type) return '';
  
  // First replace underscores with spaces
  let formatted = type.replace(/_/g, ' ');
  
  // Handle special case for 'chatbot' -> 'Chat Bot'
  if (formatted === 'chatbot') {
    return 'Chatbot';
  }

  if (formatted === 'phonecall') {
    return 'Phone';
  }

  if (formatted === 'sms') {
    return 'SMS';
  }

  if (formatted === 'social') {
    return 'Social Media';
  }

  // Split by spaces and capitalize each word
  return formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Add status mapping
export const LEAD_STATUSES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted"},
  { key: "scheduled", label: "Scheduled"},
  { key: "converted", label: "Converted"},
  { key: "noshow", label: "No Show"},
  { key: "lost", label: "Not Interested"},
];

export const LEAD_CATEGORIES = [
  { key: "prospect", label: "Prospect" },
  { key: "existing_patient", label: "Existing Patient" },
  { key: "not_applicable", label: "Not Applicable" },
];

// What the AI judged the practice should do next. Keys mirror
// Lead.AI_NEXT_ACTION_CHOICES in backend/crm/api/models.py — keep them in step.
export const AI_NEXT_ACTIONS = [
  { key: "call_back", label: "Call back" },
  { key: "send_booking_link", label: "Send booking link" },
  { key: "verify_insurance", label: "Verify insurance" },
  { key: "answer_question", label: "Answer a question" },
  { key: "awaiting_patient", label: "Waiting on patient" },
  { key: "no_action", label: "No action needed" },
];

export const LEAD_TYPES = [
  { key: "contact_form", label: "Contact Form"},
  { key: "website", label: "Website"},
  { key: "phonecall", label: "Phone"},
  { key: "chatbot", label: "Chatbot"},
  { key: "sms", label: "SMS"},
  { key: "social", label: "Social Media"},
];

export const LEAD_SOURCES = [
  { key: "google", label: "Google" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "youtube", label: "YouTube" },
  { key: "other", label: "Other Social Media" },
  { key: "preferral", label: "Patient Referral" },
  { key: "ireferral", label: "Insurance Referral" },
  { key: "sreferral", label: "Staff Referral" },
  { key: "dreferral", label: "Doctor Referral" },
  { key: "signage", label: "Signage/Walk by" },
  { key: "flyer", label: "Flyer/Postcard" },
  { key: "seminar", label: "Seminar" },
  { key: "tradeshow", label: "Tradeshow/Community Event" },
  { key: "na", label: "Not Applicable" },
];

export const LEAD_LOSS_REASONS = [
  { key: "insurance_not_accepted", label: "Insurance Not Accepted" },
  { key: "price_too_high", label: "Price Too High" },
  { key: "location_too_far", label: "Location Too Far" },
  { key: "chose_another_provider", label: "Chose Another Provider" },
  { key: "scheduling_conflict", label: "Scheduling Conflict" },
  { key: "other", label: "Other" }
];

export const NOT_APPLICABLE_REASONS = [
  { key: "spam", label: "Spam" },
  { key: "vendor", label: "Vendor" },
  { key: "insurance_provider", label: "Insurance provider" },
  { key: "unknown", label: "Unknown" },
  { key: "other", label: "Other" }
];

// Helper function to get status label
export const formatLeadStatus = (status) => {
  const statusObj = LEAD_STATUSES.find(s => s.key === status?.toLowerCase());
  return {
    key: statusObj?.key || status,
    label: statusObj?.label || status,
  };
};

// Helper function to get status color (light bg + colored text for readability)
export const getLeadStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "new":
      return "bg-teal-100 text-teal-800";
    case "contacted":
      return "bg-amber-100 text-amber-800";
    case "scheduled":
      return "bg-violet-100 text-violet-800";
    case "converted":
      return "bg-emerald-100 text-emerald-800";
    case "lost":
    case "notinterested":
      return "bg-slate-100 text-slate-600";
    case "noshow":
      return "bg-rose-100 text-rose-800";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export const getLeadCategoryColor = (category) => {
  switch (category?.toLowerCase()) {
    case "existing_patient":
      return "bg-sky-100 text-sky-800";
    case "not_applicable":
      return "bg-gray-100 text-gray-500";
    case "prospect":
    default:
      return "bg-teal-50 text-teal-700";
  }
};

// Only actions that need someone to do something get colour; "no action" and
// "waiting on patient" stay grey so the list reads as a worklist.
export const getAiNextActionColor = (action) => {
  switch (action) {
    case "call_back":
      return "bg-amber-100 text-amber-800";
    case "send_booking_link":
      return "bg-teal-50 text-teal-700";
    case "verify_insurance":
      return "bg-sky-100 text-sky-800";
    case "answer_question":
      return "bg-indigo-50 text-indigo-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

export const formatAiNextAction = (action) => {
  const found = AI_NEXT_ACTIONS.find((a) => a.key === action);
  return found ? found.label : null;
};

export const formatLeadCategory = (category) => {
  const catObj = LEAD_CATEGORIES.find(c => c.key === category?.toLowerCase());
  return {
    key: catObj?.key || category,
    label: catObj?.label || category,
  };
};

// Helper function to format loss reasons
export const formatLossReason = (lossReason) => {
  // Check both LEAD_LOSS_REASONS and NOT_APPLICABLE_REASONS
  const reasonObj = LEAD_LOSS_REASONS.find(r => r.key === lossReason) || 
                   NOT_APPLICABLE_REASONS.find(r => r.key === lossReason);
  return reasonObj?.label || lossReason;
};

export const formatNoteContent = (content) => {
  if (!content || typeof content !== 'string') {
    return content || '';
  }

  const requiredFields = ['Caller', 'To', 'CallStatus']; // Core fields to identify call data
  const displayFields = [
    'Caller',
    'To',
    'CallStatus',
    'Direction',
    'Called',
    'ToState',
    'CallerCountry',
    'CallerState',
    'ToZip',
    'ToCountry',
    'CalledZip',
    'CalledCity',
    'From',
    'CalledCountry',
    'CallerCity',
    'ToCity',
    'FromCountry',
    'FromCity',
    'CalledState',
    'FromZip',
    'FromState',
  ];

  try {
    const parsed = JSON.parse(content);
    // Check if it looks like call data (has required fields)
    if (requiredFields.every(field => field in parsed)) {
      return `Call Details:\n${displayFields
        .filter(field => field in parsed) // Only include fields present in JSON
        .map(field => `- ${field}: ${parsed[field]}`)
        .join('\n')}`;
    }
    // For other JSON, return original content as string
    return content;
  } catch (e) {
    // Not JSON, return as is
    return content;
  }
};

export const formatPhoneNumber = (phone) => {
  if (!phone) return "N/A";

  // Remove any non-digit characters except the leading +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // Track if there's a leading +
  const hasPlus = cleaned.startsWith('+');
  if (hasPlus) {
    cleaned = cleaned.slice(1);
  }

  // Try matching with country code: 1-3 digits + 10 digits
  let match = cleaned.match(/^(\d{1,3})(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${hasPlus ? '+' : '+'}${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
  }
  // Try matching without country code: exactly 10 digits
  match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `${hasPlus ? '+' : '+1'} ${match[1]} ${match[2]} ${match[3]}`;
  }
  return phone; // Return original if format doesn't match
};

// Single source of truth for how a phone-call lead's outcome is displayed,
// shared by the leads list row badge and the lead detail header so they never
// drift. Returns null for non-phone leads or calls with no usable status yet,
// so callers can simply skip rendering.
//
// Two underlying signals:
//   - call_status: Twilio DialCallStatus (completed / no-answer / busy / failed
//     / canceled / "" = still ringing or a legacy pre-tracking lead).
//   - call_outcome: AI transcript classification of a *completed* call
//     (human_answered / message_left / empty / "" = not yet classified).
// `completed` is ambiguous on its own — the office answering machine picking up
// also reports completed — so call_outcome is what separates a real conversation
// from a voicemail left on the machine. `needsCallback` mirrors the backend
// filter_needs_callback() logic.
export const getCallOutcomeDisplay = (lead) => {
  if (!lead || lead.type !== "phonecall") return null;

  const status = lead.call_status || "";
  const outcome = lead.call_outcome || "";

  // Never connected to anyone (not even the machine) — caller needs a callback.
  if (status === "no-answer")
    return { label: "Missed call", className: "bg-red-100 text-red-800", needsCallback: true };
  if (status === "busy")
    return { label: "Line busy", className: "bg-red-100 text-red-800", needsCallback: true };
  if (status === "failed")
    return { label: "Call failed", className: "bg-red-100 text-red-800", needsCallback: true };
  // Caller hung up while it was still ringing — usually a misdial, not a callback.
  if (status === "canceled")
    return { label: "Hang-up", className: "bg-gray-100 text-gray-600", needsCallback: false };

  if (status === "completed") {
    if (outcome === "message_left")
      return { label: "Message left", className: "bg-amber-100 text-amber-800", needsCallback: true };
    if (outcome === "human_answered")
      return { label: "Answered", className: "bg-emerald-100 text-emerald-800", needsCallback: false };
    if (outcome === "empty")
      return { label: "No message", className: "bg-gray-100 text-gray-600", needsCallback: false };
    // Connected but not yet classified (short call, or AI still processing).
    return { label: "Connected", className: "bg-sky-100 text-sky-800", needsCallback: false };
  }

  // Blank status: call still in progress, or a legacy lead from before tracking.
  return null;
};
